import { useEffect, useState, type FormEvent } from 'react'
import type { BudgetCategory, BudgetMonth, GoalCadence, GoalType } from './types'
import { formatCurrency } from './format'
import { useAssignMoney, useUpdateCategoryGoal } from './hooks'
import { QuickAssignSection } from './QuickAssignSection'

interface Props {
  open: boolean
  onClose: () => void
  category: BudgetCategory | null
  priorMonths: Array<BudgetMonth | null | undefined>
  year: number
  monthNumber: number
}

/**
 * Right-side drawer for setting, editing, and acting on a category's
 * goal. Mirrors YNAB's "Target" panel: pick a type, fill in the
 * fields that type uses, see the derived month-needed, and one-click
 * fund the gap.
 */
export function GoalDrawer({ open, onClose, category, priorMonths, year, monthNumber }: Props) {
  const updateGoal = useUpdateCategoryGoal()
  const assign = useAssignMoney()

  const [goalType, setGoalType] = useState<GoalType | ''>('')
  const [target, setTarget] = useState('')
  const [cadence, setCadence] = useState<GoalCadence>('monthly')
  const [day, setDay] = useState('')
  const [goalMonth, setGoalMonth] = useState('')
  const [monthlyContribution, setMonthlyContribution] = useState('')
  const [targetMonth, setTargetMonth] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [seededFor, setSeededFor] = useState<string | null>(null)

  // Seed from the category when the drawer opens onto a new row.
  // Keyed by id (not object identity) so live refetches after an
  // assign don't clobber in-progress form edits.
  useEffect(() => {
    if (!open || !category) {
      setSeededFor(null)
      return
    }
    if (seededFor === category.id) return
    setSeededFor(category.id)
    setGoalType(category.goalType ?? '')
    setTarget(category.target != null ? String(category.target) : '')
    setCadence(category.goalCadence ?? 'monthly')
    setDay(category.goalDay != null ? String(category.goalDay) : '')
    setGoalMonth(category.goalMonth != null ? String(category.goalMonth) : '')
    setMonthlyContribution(
      category.goalMonthlyContribution != null ? String(category.goalMonthlyContribution) : '',
    )
    setTargetMonth(category.targetMonth ?? '')
    setNote(category.note ?? '')
    setError(null)
  }, [open, category, seededFor])

  if (!category) return null

  function parseDollars(value: string): number | null {
    const n = Number(value.replace(/[$,]/g, ''))
    return Number.isFinite(n) && n > 0 ? n : null
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!category || !goalType) return
    const t = parseDollars(target)
    if (!t) {
      setError('Enter a positive target amount')
      return
    }
    const dayNum = day ? Math.min(31, Math.max(1, parseInt(day, 10))) : null
    const monthly = goalType === 'MF' || goalType === 'DEBT' ? parseDollars(monthlyContribution) : null
    updateGoal.mutate(
      {
        year,
        month: monthNumber,
        categoryId: category.id,
        fields: {
          goalType,
          target: t,
          goalCadence: goalType === 'NEED' ? cadence : null,
          goalDay: goalType === 'NEED' ? dayNum : null,
          goalMonth:
            goalType === 'NEED' && cadence === 'yearly' && goalMonth
              ? parseInt(goalMonth, 10)
              : null,
          goalMonthlyContribution: monthly,
          targetMonth: goalType === 'TB' || goalType === 'DEBT' ? targetMonth || null : null,
          note: note.trim() || null,
        },
      },
      { onSuccess: () => onClose(), onError: (err) => setError(String(err)) },
    )
  }

  function deleteTarget() {
    if (!category) return
    updateGoal.mutate(
      {
        year,
        month: monthNumber,
        categoryId: category.id,
        fields: {
          goalType: null,
          target: null,
          goalCadence: null,
          goalDay: null,
          goalMonth: null,
          goalMonthlyContribution: null,
          targetMonth: null,
          note: note.trim() || null,
        },
      },
      { onSuccess: () => onClose(), onError: (err) => setError(String(err)) },
    )
  }

  function fundNow() {
    if (!category) return
    const owed = category.targetUnderfunded ?? 0
    if (owed <= 0) return
    assign.mutate(
      { year, month: monthNumber, categoryId: category.id, newAssigned: category.assigned + owed },
      { onSuccess: () => onClose() },
    )
  }

  const owed = category.targetUnderfunded ?? 0

  // Non-modal slide-over: no backdrop, the page behind stays
  // interactive (click another row to retarget the drawer). Stays
  // mounted while closed so the slide-out can animate.
  return (
    <aside
      role="dialog"
      aria-labelledby="goal-drawer-title"
      aria-hidden={!open}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      className={`fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-zinc-200 bg-white shadow-2xl transition-transform duration-200 ease-out dark:border-zinc-800 dark:bg-zinc-950 ${
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
    >
        <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <div>
            <h2 id="goal-drawer-title" className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
              Target
            </h2>
            <div className="text-lg font-semibold">{category.name}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close target panel"
            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            ✕
          </button>
        </header>

        <section className="space-y-2 border-b border-zinc-200 px-4 py-3 text-sm dark:border-zinc-800">
          <Row label="Assigned this month" value={formatCurrency(category.assigned)} />
          <Row label="Activity" value={formatCurrency(category.activity)} />
          <Row label="Available" value={formatCurrency(category.available)} bold />
          {owed > 0 && (
            <button
              type="button"
              onClick={fundNow}
              disabled={assign.isPending}
              className="mt-2 w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {assign.isPending ? 'Funding…' : `Fund to target (${formatCurrency(owed)})`}
            </button>
          )}
        </section>

        <QuickAssignSection
          category={category}
          priorMonths={priorMonths}
          busy={assign.isPending}
          onAssign={(v) => {
            if (!category) return
            assign.mutate({ year, month: monthNumber, categoryId: category.id, newAssigned: v })
          }}
          onUseAsTarget={(amount) => {
            if (!goalType) {
              setGoalType('NEED')
              setCadence('monthly')
            }
            setTarget(String(amount))
          }}
        />

        <form onSubmit={submit} className="flex flex-1 flex-col gap-4 px-4 py-4 text-sm">
          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Target type
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  { id: 'NEED', label: 'Needed for spending', hint: 'Recurring monthly/weekly/yearly target' },
                  { id: 'MF', label: 'Monthly funding', hint: 'Save X each month until target reached' },
                  { id: 'TB', label: 'Targeted balance', hint: 'Maintain or reach a balance' },
                  { id: 'DEBT', label: 'Debt payoff', hint: 'Monthly chunk toward a payoff' },
                ] as Array<{ id: GoalType; label: string; hint: string }>
              ).map((t) => {
                const selected = goalType === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setGoalType(t.id)}
                    className={`rounded-md border px-3 py-2 text-left ${
                      selected
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/40'
                        : 'border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900'
                    }`}
                  >
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-zinc-500">{t.hint}</div>
                  </button>
                )
              })}
            </div>
          </fieldset>

          {goalType && (
            <>
              <Field label="Target amount">
                <input
                  type="text"
                  inputMode="decimal"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="e.g. 500"
                  required
                  className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 tabular-nums dark:border-zinc-700 dark:bg-zinc-950"
                />
              </Field>

              {goalType === 'NEED' && (
                <>
                  <Field label="How often?">
                    <select
                      value={cadence}
                      onChange={(e) => setCadence(e.target.value as GoalCadence)}
                      className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
                    >
                      <option value="monthly">Every month</option>
                      <option value="weekly">Every week</option>
                      <option value="yearly">Every year</option>
                    </select>
                  </Field>
                  {cadence === 'yearly' && (
                    <Field label="Due month (every year)">
                      <select
                        value={goalMonth}
                        onChange={(e) => setGoalMonth(e.target.value)}
                        className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
                      >
                        <option value="">No due month (spread over 12)</option>
                        {MONTH_NAMES.map((name, i) => (
                          <option key={name} value={i + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                  {cadence === 'monthly' && (
                    <Field label="Day of month (optional)">
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={day}
                        onChange={(e) => setDay(e.target.value)}
                        placeholder="e.g. 15"
                        className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 tabular-nums dark:border-zinc-700 dark:bg-zinc-950"
                      />
                    </Field>
                  )}
                </>
              )}

              {(goalType === 'MF' || goalType === 'DEBT') && (
                <Field label="Set aside each month (optional)">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={monthlyContribution}
                    onChange={(e) => setMonthlyContribution(e.target.value)}
                    placeholder={`Defaults to ${target || 'target'}`}
                    className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 tabular-nums dark:border-zinc-700 dark:bg-zinc-950"
                  />
                </Field>
              )}

              {(goalType === 'TB' || goalType === 'DEBT') && (
                <Field label="Target month (optional)">
                  <input
                    type="month"
                    value={targetMonth}
                    onChange={(e) => setTargetMonth(e.target.value)}
                    className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
                  />
                </Field>
              )}

              <Field label="Note">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="optional"
                  className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
                />
              </Field>
            </>
          )}

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <div className="mt-auto flex gap-2 border-t border-zinc-200 pt-4 dark:border-zinc-800">
            {category.goalType != null && (
              <button
                type="button"
                onClick={deleteTarget}
                disabled={updateGoal.isPending}
                aria-label={`Delete target for ${category.name}`}
                className="rounded-md border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40"
              >
                Delete target
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateGoal.isPending || !goalType}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {updateGoal.isPending ? 'Saving…' : 'Save target'}
            </button>
          </div>
        </form>
    </aside>
  )
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-zinc-600 dark:text-zinc-300">{label}</span>
      {children}
    </label>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={`tabular-nums ${bold ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  )
}
